/**
 * What the strike check found, in order and in words (Arthur, 21 October 2026, 11:21): whatever it marks struck sorts
 * to the top of a document's lists, struck through first, then partly struck, then a power with initials beside it;
 * each says so in WORDS, never by colour alone. Shared by the one-document review (ReadingReview.tsx) and its test.
 */
export interface StrikeField { field: string; value: string | null; page: number | null; quote: string | null; drop_reason: string | null }

const isPower = (f: StrikeField) => f.field === 'power_granted' || f.field === 'power_struck';

/** A power's own answer, by its page and quote; failing that, its page not looked at, with why. */
export function strikeAnswerOf(f: StrikeField, rows: readonly StrikeField[]): StrikeField | undefined {
  return rows.find((s) => (s.field === 'power_strike' || s.field === 'power_strike_unchecked')
    && s.page === f.page && s.quote != null && s.quote === f.quote)
    ?? rows.find((s) => s.field === 'strike_unchecked' && s.page != null && s.page === f.page);
}

/** 0 struck through, 1 partly struck, 2 initials beside it, 3 the rest. */
export function strikeRank(f: StrikeField, rows: readonly StrikeField[], initials: (f: StrikeField) => boolean): number {
  const s = isPower(f) ? strikeAnswerOf(f, rows) : undefined;
  if (s?.field === 'power_strike' && s.value === 'all') return 0;
  if (s?.field === 'power_strike' && s.value === 'part') return 1;
  return f.field === 'power_granted' && initials(f) ? 2 : 3;
}

/** The list with what is struck first, by rank; within a rank, the order it came in. */
export function struckFirst<T extends StrikeField>(list: readonly T[], rows: readonly StrikeField[], initials: (f: StrikeField) => boolean): T[] {
  return list.map((f, i) => ({ f, i })).sort((x, y) => strikeRank(x.f, rows, initials) - strikeRank(y.f, rows, initials) || x.i - y.i).map((x) => x.f);
}

/** The words that open a struck power's line. */
export const STRUCK_WORDS = { all: 'Struck through', part: 'Partly struck' } as const;
/** The words that head a power ending in initials. */
export const initialsHeading = (page: number | null) => `Initials beside it, check page ${page ?? '?'}`;
/** A struck commission or letters date, as the screen shows it. */
export const struckDateLine = (quote: string | null) => `Struck through: ${quote ?? ''}`;
